import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import moment from 'moment'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import asyncAction from 'modules/core/lib/asyncAction'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
// Hooks
import { usePortal } from 'modules/core/lib/portalContext'

import type Employee from 'modules/employees/store/models/Employee'
import useForm, { FormErrorProvider } from 'modules/timeoff/hooks/useForm'
import allowances from 'modules/timeoff/store/collections/allowances'
import cycles from 'modules/timeoff/store/collections/cycles'
import allowanceStats from 'modules/timeoff/store/collections/stats'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type AllowanceIncidence from 'modules/timeoff/store/models/AllowanceIncidence'
import type Cycle from 'modules/timeoff/store/models/Cycle'
import type { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

import ModalContent from './ModalContent'
import useUpsertIncidence from './useUpsertIncidence'

interface DeleteProps {
  incidence?: AllowanceIncidence
  onSuccess: () => void
}

const DeleteButton = ({ incidence, onSuccess }: DeleteProps) => {
  const { closePortal } = usePortal()

  const { running: deleting, performAction: onDelete } = useAsyncAction(async () => {
    if (!incidence) return

    const { success } = await asyncAction(() => incidence.destroy())

    if (success) {
      closePortal()
      onSuccess()
    }
  })

  if (!incidence) return null

  return (
    <Button
      onClick={onDelete}
      disabled={deleting}
      type="negative"
      extra={{ 'data-intercom-target': 'timeoff-incidences-delete-button' }}
    >
      {i18n.t('time_off.incidences.delete')}
    </Button>
  )
}

const updateStats = (employee: Employee, allowance: Allowance, cycle: Cycle) => {
  const referenceDate = cycle.referenceDate

  const allowanceStat = allowanceStats.findOrInitializeBy(employee, allowance, referenceDate)
  allowanceStat.fetch()
}

interface Props {
  employee: Employee
  onClose: () => void
  incidence?: AllowanceIncidence
  timelineItem: PolicyTimelineItem
}

const AllowanceIncidenceModal = ({ employee, onClose, incidence, timelineItem }: Props) => {
  const firstUpdate = React.useRef(true)
  const [formError, setFormError] = React.useState(false)

  const [initialCycle, setInitialCycle] = React.useState<Cycle | null | undefined>()

  const form = useForm({
    incidence,
    employee,
  })

  const employeeId = employee.attributes.id
  const policyId = timelineItem.timeoff_policy_id
  const allowanceId = +form.get('timeoff_allowance_id').value

  const formEffectiveOn = form.get('effective_on').value
  const timelineEnd = timelineItem?.end_at
  const effectiveOn =
    (formEffectiveOn && stringToDate(formEffectiveOn, 'backend').startOf('day')) ||
    moment(timelineEnd) ||
    moment()
  const { loading: loadingCycles } = useAsyncData(async () => {
    const allowance = allowances.get(allowanceId)
    allowance ? await cycles.fetchCycles(allowanceId, employeeId, effectiveOn) : cycles.reset([])

    if (firstUpdate.current) {
      firstUpdate.current = false

      const cycleBeforeUpdate = cycles.findByEffectiveOn(allowanceId, effectiveOn)
      setInitialCycle(cycleBeforeUpdate)

      form.setValues({
        timeoff_allowance_id: allowance?.attributes.id,
      })
    }

    const referenceDate = timelineEnd ? moment(timelineEnd) : moment()
    const newCycle =
      cycles.findByEffectiveOn(allowanceId, effectiveOn) ||
      cycles.findByEffectiveOn(allowanceId, referenceDate) ||
      cycles.at(0)

    const newEffectiveOn = newCycle?.start
    if (newEffectiveOn) {
      form.setValues({
        effective_on: dateToString(newEffectiveOn, 'backend'),
      })
    }
  }, [employeeId, policyId, allowanceId, timelineEnd])

  const {
    loading: loadingManualAdjustmentOptions,
    featureAvailability: manualAdjustmentOptionsAvailability,
  } = useFeatureAvailability(Features.timeoff_manual_adjustment_options)

  const hasManualAdjustmentOptions = manualAdjustmentOptionsAvailability.available

  const loading = loadingCycles || loadingManualAdjustmentOptions

  const title = incidence ? i18n.t('time_off.incidences.edit') : i18n.t('time_off.incidences.add')

  const submitLabel = incidence
    ? i18n.t('time_off.incidences.submits.edit_label')
    : i18n.t('time_off.incidences.submits.add_label')

  const onSuccess = () => {
    const formEffectiveOn = form.get('effective_on').value
    const effectiveOn = stringToDate(formEffectiveOn, 'backend').startOf('day')
    const timeoffAllowanceId = +form.get('timeoff_allowance_id').value
    const allowance = allowances.get(timeoffAllowanceId)
    const cycle = cycles.findByEffectiveOn(timeoffAllowanceId, effectiveOn)

    if (cycle && allowance) updateStats(employee, allowance, cycle)

    const initialAllowance = allowances.get(initialCycle?.attributes.allowance_id)

    if (initialCycle && initialAllowance && initialCycle !== cycle) {
      updateStats(employee, initialAllowance, initialCycle)
    }
  }

  const { submitting, onSubmit } = useUpsertIncidence({
    form,
    incidence,
    onSuccess,
  })

  const description = i18n.t('time_off.incidences.description_by_hours')

  const disableButton = submitting || formError || form.validateInput

  return (
    <Modal
      title={title}
      illustration="user"
      description={description}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          <DeleteButton incidence={incidence} onSuccess={onSuccess} />

          <Button
            disabled={disableButton}
            submit
            extra={{ 'data-intercom-target': 'timeoff-incidences-submit-button' }}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <FormErrorProvider action={setFormError}>
        {loading && <Loading />}
        {!loading && (
          <ModalContent
            form={form}
            policyId={policyId}
            hasManualAdjustmentOptions={hasManualAdjustmentOptions}
          />
        )}
      </FormErrorProvider>
    </Modal>
  )
}

export default observer(AllowanceIncidenceModal)
